// src/ContactFormPage.js
import React, { useState, useEffect, useRef } from "react";
import emailjs from "emailjs-com";
import { useNavigate } from "react-router-dom"; // useNavigate hook to navigate
import "./CustomForm.scss"; // Styling for the contact form page
import { useTranslation } from "react-i18next";
import editIcon from "../../assets/edit.png";
import deleteIcon from "../../assets/delete.png";
import arrowDown from "../../assets/down-arrow.png";
import { DateRange } from "react-date-range";
import { enUS } from "date-fns/locale"; // <-- import this
import { format, differenceInCalendarDays } from "date-fns";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css";

const ACCOMMODATION_RATES = [
  { rate: 35, labelKey: "contactForm.accommodationTriple" },
  { rate: 40, labelKey: "contactForm.accommodationDouble" },
  { rate: 45, labelKey: "contactForm.accommodationSingle" },
];
const DEFAULT_ACCOMMODATION_RATE = 35;
const PARTICIPATION_FEE = 80;

const AccommodationTypeSelector = ({ selectedRate, onSelect }) => {
  const { t } = useTranslation();

  return (
    <div className="accommodation-selector-block">
      <div className="accommodation-type-selector">
        {ACCOMMODATION_RATES.map(({ rate, labelKey }) => (
          <button
            key={labelKey}
            type="button"
            className={`accommodation-type-btn${
              selectedRate === rate ? " selected" : ""
            }`}
            onClick={() => onSelect(rate)}
          >
            <span className="accommodation-type-btn__label">{t(labelKey)}</span>
          </button>
        ))}
      </div>
      <p className="accommodation-breakfast-note">
        {t("contactForm.accommodationBreakfastNote")}
      </p>
    </div>
  );
};

const CustomForm = () => {
  const [applicationType, setApplicationType] = useState("team");
  const [isOpen, setIsOpen] = useState(false);
  const [pricingPlan, setPricingPlan] = useState("regular");
  // team member
  const [isAddingMember, setIsAddingMember] = useState(false);
  const defaultRange = [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      color: "#00ABFF", // Important if you're using `rangeColors`
    },
  ];
  const [newMember, setNewMember] = useState({
    name: "",
    age: "",
    belt: "",
    pricingPlan: "regular",
    bookingPeriod: "",
    dateRange: defaultRange,
    price: 80,
  });
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editedMember, setEditedMember] = useState({
    name: "",
    age: "",
    belt: "",
    pricingPlan: "regular",
    bookingPeriod: "",
    dateRange: defaultRange,
    price: 80,
  });
  // coach
  const [isAddingCoach, setIsAddingCoach] = useState(false);
  const [newCoach, setNewCoach] = useState({
    name: "",
    pricingPlan: "regular",
    bookingPeriod: "",
    dateRange: defaultRange,
    price: 80,
  });
  const [editingCoachId, setEditingCoachId] = useState(null);
  const [editedCoach, setEditedCoach] = useState({
    name: "",
    pricingPlan: "regular",
    bookingPeriod: "",
    dateRange: defaultRange,
    price: 80,
  });
  // booking
  const [numOfNights, setNumOfNights] = useState(0);
  const [accommodationRate, setAccommodationRate] = useState(
    DEFAULT_ACCOMMODATION_RATE
  );
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
      color: "#00ABFF",
    },
  ]);
  const [totalAmount, setTotalAmount] = useState(0);

  const dropdownRef = useRef(null);
  const form = useRef();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const options = ["regular", "full"];

  const { t } = useTranslation();
  const navigate = useNavigate(); // Hook to navigate back

  const calcAccommodationPrice = (nights, rate) => nights * rate;
  const calcFullPackagePrice = (nights, rate) =>
    calcAccommodationPrice(nights, rate) + PARTICIPATION_FEE;

  const initialFormData = {
    applicationType: applicationType,
    // team
    clubName: "",
    // coachName: "",
    clubPhone: "",
    clubEmail: "",
    coaches: [],
    teamMembers: [],
    // individual
    individualName: "",
    individualClubName: "",
    individualAge: "",
    individualBelt: "",
    individualPhone: "",
    individualEmail: "",
    individualBookingPeriod: "",
    individualDateRange: defaultRange,
    individualPricingPlan: "regular",
    // volunteer
    volunteerName: "",
    volunteerClubName: "",
    volunteerAge: "",
    volunteerPhone: "",
    volunteerEmail: "",
    // pricing
    // individual is using total amount
    totalAmount: totalAmount,
    // pricingPlan: pricingPlan,
  };

  const [formData, setFormData] = useState({
    applicationType: applicationType,
    // team
    clubName: "",
    // coachName: "",
    clubPhone: "",
    clubEmail: "",
    coaches: [],
    teamMembers: [],
    // individual
    individualName: "",
    individualClubName: "",
    individualAge: "",
    individualBelt: "",
    individualPhone: "",
    individualEmail: "",
    // volunteer
    volunteerName: "",
    volunteerClubName: "",
    volunteerAge: "",
    volunteerPhone: "",
    volunteerEmail: "",
    // pricing
    totalAmount: totalAmount,
    // pricingPlan: pricingPlan,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (applicationType !== "individual") {
      calculateTotalAmount();
    }

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        handleGoBack();
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [formData.coaches, formData.teamMembers]);

  const handleGoBack = () => {
    navigate("/"); // Navigate to the home page
  };

  const handleBookingPeriodChange = (item) => {
    const start = item.selection.startDate;
    const end = item.selection.endDate;
    const nights = differenceInCalendarDays(end, start);
    const numOfNights = nights > 0 ? nights : 0;
    const newRange = [
      {
        ...item.selection,
        key: "selection",
        color: "#00ABFF", // This is critical for react-date-range
      },
    ];
    setDateRange(newRange);
    setNumOfNights(numOfNights);

    const formattedStart = format(start, "dd MMMM");
    const formattedEnd = format(end, "dd MMMM");

    if (isAddingCoach) {
      setNewCoach((prevCoach) => ({
        ...prevCoach,
        bookingPeriod: `${formattedStart} - ${formattedEnd}`,
        dateRange: newRange,
        accommodationRate,
        price: calcFullPackagePrice(numOfNights, accommodationRate),
      }));
    } else if (isAddingMember) {
      setNewMember((prevMember) => ({
        ...prevMember,
        bookingPeriod: `${formattedStart} - ${formattedEnd}`,
        dateRange: newRange,
        accommodationRate,
        price: calcFullPackagePrice(numOfNights, accommodationRate),
      }));
    }
  };

  const handleIndividualBookingPeriodChange = (item) => {
    const start = item.selection.startDate;
    const end = item.selection.endDate;
    const formattedStart = format(start, "dd MMMM");
    const formattedEnd = format(end, "dd MMMM");
    const nights = differenceInCalendarDays(end, start);
    const numOfNights = nights > 0 ? nights : 0;
    const newRange = [
      {
        ...item.selection,
        key: "selection",
        color: "#00ABFF", // This is critical for react-date-range
      },
    ];
    setDateRange(newRange);
    setNumOfNights(numOfNights);
    setTotalAmount(calcFullPackagePrice(numOfNights, accommodationRate));

    setFormData((prevData) => ({
      ...prevData,
      individualBookingPeriod: `${formattedStart} - ${formattedEnd}`,
      individualDateRange: newRange,
      totalAmount: calcFullPackagePrice(numOfNights, accommodationRate),
    }));
  };

  const handleAccommodationRateChange = (rate) => {
    setAccommodationRate(rate);
    if (isAddingCoach && pricingPlan === "full" && numOfNights > 0) {
      setNewCoach((prevCoach) => ({
        ...prevCoach,
        accommodationRate: rate,
        price: calcFullPackagePrice(numOfNights, rate),
      }));
    } else if (isAddingMember && pricingPlan === "full" && numOfNights > 0) {
      setNewMember((prevMember) => ({
        ...prevMember,
        accommodationRate: rate,
        price: calcFullPackagePrice(numOfNights, rate),
      }));
    }
  };

  const handleIndividualAccommodationRateChange = (rate) => {
    setAccommodationRate(rate);
    if (pricingPlan === "full" && numOfNights > 0) {
      const total = calcFullPackagePrice(numOfNights, rate);
      setTotalAmount(total);
      setFormData((prevData) => ({
        ...prevData,
        totalAmount: total,
      }));
    }
  };

  const handleEditAccommodationRateChange = (rate) => {
    if (editingCoachId) {
      setEditedCoach((prevCoach) => ({
        ...prevCoach,
        accommodationRate: rate,
        price:
          prevCoach.pricingPlan === "full" && numOfNights > 0
            ? calcFullPackagePrice(numOfNights, rate)
            : prevCoach.price,
      }));
    } else if (editingMemberId) {
      setEditedMember((prevMember) => ({
        ...prevMember,
        accommodationRate: rate,
        price:
          prevMember.pricingPlan === "full" && numOfNights > 0
            ? calcFullPackagePrice(numOfNights, rate)
            : prevMember.price,
      }));
    }
  };

  const calculateTotalAmount = () => {
    const coachTotal = formData.coaches.reduce(
      (sum, coach) => sum + (Number(coach.price) || 0),
      0
    );
    const memberTotal = formData.teamMembers.reduce(
      (sum, member) => sum + (Number(member.price) || 0),
      0
    );

    const total = coachTotal + memberTotal;

    setFormData((prevData) => ({
      ...prevData,
      totalAmount: total,
    }));
    setTotalAmount(total);
  };

  const handleEditBookingPeriodChange = (item) => {
    const start = item.selection.startDate;
    const end = item.selection.endDate;
    const nights = differenceInCalendarDays(end, start);
    const numOfNights = nights > 0 ? nights : 0;
    const newRange = [
      {
        ...item.selection,
        key: "selection",
        color: "#00ABFF", // This is critical for react-date-range
      },
    ];
    setDateRange(newRange);
    setNumOfNights(numOfNights);

    const formattedStart = format(start, "dd MMMM");
    const formattedEnd = format(end, "dd MMMM");

    const editRate = editingCoachId
      ? editedCoach.accommodationRate ?? DEFAULT_ACCOMMODATION_RATE
      : editedMember.accommodationRate ?? DEFAULT_ACCOMMODATION_RATE;

    if (editingCoachId) {
      setEditedCoach((prevCoach) => ({
        ...prevCoach,
        bookingPeriod: `${formattedStart} - ${formattedEnd}`,
        dateRange: newRange,
        price: calcFullPackagePrice(numOfNights, editRate),
      }));
    } else if (editingMemberId) {
      setEditedMember((prevMember) => ({
        ...prevMember,
        bookingPeriod: `${formattedStart} - ${formattedEnd}`,
        dateRange: newRange,
        price: calcFullPackagePrice(numOfNights, editRate),
      }));
    }
  };

  const handleApplicationTypeChange = (type) => {
    setApplicationType(type);

    setPricingPlan("regular");
    setFormData(() => ({
      ...initialFormData,
      applicationType: type,
      // pricingPlan: "regular",
    }));
    if (type === "individual") {
      setTotalAmount(80);
    }
  };

  const handlePricingPlanChange = (option) => {
    setPricingPlan(option);
    if (isAddingCoach) {
      if (option === "regular") {
        restoreDatepicker();

        setNewCoach((prevCoach) => ({
          ...prevCoach,
          pricingPlan: option,
          bookingPeriod: "",
          dateRange: defaultRange,
          price: 80,
        }));
      } else {
        setNewCoach((prevCoach) => ({
          ...prevCoach,
          pricingPlan: option,
        }));
      }
    } else if (isAddingMember) {
      if (option === "regular") {
        restoreDatepicker();
        setNewMember((prevMember) => ({
          ...prevMember,
          pricingPlan: option,
          bookingPeriod: "",
          dateRange: defaultRange,
          price: 80,
        }));
      } else {
        setNewMember((prevMember) => ({
          ...prevMember,
          pricingPlan: option,
        }));
      }
    }
    setIsOpen(false);
  };

  const handleIndividualPricingPlanChange = (option) => {
    setPricingPlan(option);

    if (option === "regular") {
      restoreDatepicker();
      setTotalAmount(80);
      setFormData((prevData) => ({
        ...prevData,
        individualPricingPlan: option,
        individualBookingPeriod: "",
        individualDateRange: defaultRange,
        totalAmount: 80,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        individualPricingPlan: option,
      }));
    }
    setIsOpen(false);
  };

  const handleEditPricingPlanChange = (option) => {
    setPricingPlan(option);
    if (editingCoachId) {
      if (option === "regular") {
        restoreDatepicker();

        setEditedCoach((prevCoach) => ({
          ...prevCoach,
          pricingPlan: option,
          bookingPeriod: "",
          dateRange: defaultRange,
          price: 80,
        }));
      } else {
        setEditedCoach((prevCoach) => ({
          ...prevCoach,
          pricingPlan: option,
        }));
      }
    } else if (editingMemberId) {
      if (option === "regular") {
        restoreDatepicker();
        setEditedMember((prevMember) => ({
          ...prevMember,
          pricingPlan: option,
          bookingPeriod: "",
          dateRange: defaultRange,
          price: 80,
        }));
      } else {
        setEditedMember((prevMember) => ({
          ...prevMember,
          pricingPlan: option,
        }));
      }
    }
    setIsOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // TEAM application functions
  const handleShowAddCoach = () => {
    setIsAddingCoach(true);
    setEditingCoachId(null); // Ensure edit input is closed

    setNewCoach({
      name: "",
      pricingPlan: "regular",
      bookingPeriod: "",
      dateRange: defaultRange,
      price: 80,
    });
  };

  const handleShowAddMember = () => {
    setIsAddingMember(true);
    setEditingMemberId(null); // Ensure edit input is closed
    setNewMember({
      name: "",
      age: "",
      belt: "",
      pricingPlan: "regular",
      bookingPeriod: "",
      dateRange: defaultRange,
      price: 80,
    });
  };

  const handleNewCoachChange = (e) => {
    const { name, value } = e.target;
    setNewCoach((prevCoach) => ({
      ...prevCoach,
      [name]: value,
    }));
  };

  const handleNewMemberChange = (e) => {
    const { name, value } = e.target;
    setNewMember((prevMember) => ({
      ...prevMember,
      [name]: value,
    }));
  };

  const restoreDatepicker = () => {
    setNumOfNights(0);
    setDateRange(defaultRange);
    setPricingPlan("regular");
    setAccommodationRate(DEFAULT_ACCOMMODATION_RATE);
  };

  const handleAddCoach = () => {
    if (newCoach.name.trim()) {
      setFormData((prevData) => ({
        ...prevData,
        coaches: [...prevData.coaches, { id: Date.now(), ...newCoach }],
      }));

      restoreDatepicker();
      setIsAddingCoach(false);
      setNewCoach({
        name: "",
        pricingPlan: "regular",
        bookingPeriod: "",
        dateRange: defaultRange,
        price: 80,
      });
    }
  };

  const handlecancelAddCoach = () => {
    restoreDatepicker();
    setIsAddingCoach(false);
  };

  const handleAddTeamMember = () => {
    if (newMember.name.trim()) {
      setFormData((prevData) => ({
        ...prevData,
        teamMembers: [
          ...prevData.teamMembers,
          { id: Date.now(), ...newMember },
        ],
      }));

      // restore defaults
      setNumOfNights(0);
      setIsAddingMember(false);
      setDateRange(defaultRange);
      setPricingPlan("regular");
      setNewMember({
        name: "",
        age: "",
        belt: "",
        pricingPlan: "regular",
        bookingPeriod: "",
        dateRange: defaultRange,
        price: 80,
      });
    }
  };

  const handlecancelAddMember = () => {
    restoreDatepicker();
    setIsAddingMember(false);
  };

  const handleShowEditCoach = (coach) => {
    const nights = differenceInCalendarDays(
      coach.dateRange[0]?.endDate,
      coach.dateRange[0]?.startDate
    );
    const numOfNights = nights > 0 ? nights : 0;

    setNumOfNights(numOfNights);
    setEditingCoachId(coach.id);
    setEditedCoach({
      ...coach,
      accommodationRate:
        coach.accommodationRate ?? DEFAULT_ACCOMMODATION_RATE,
    });
    setIsAddingCoach(false); // Ensure add input is closed
  };

  const handleShowEditMember = (member) => {
    const nights = differenceInCalendarDays(
      member.dateRange[0]?.endDate,
      member.dateRange[0]?.startDate
    );
    const numOfNights = nights > 0 ? nights : 0;

    setNumOfNights(numOfNights);
    setEditingMemberId(member.id);
    setEditedMember({
      ...member,
      accommodationRate:
        member.accommodationRate ?? DEFAULT_ACCOMMODATION_RATE,
    });
    setIsAddingMember(false); // Ensure add input is closed
  };

  const handleEditedCoachChange = (e) => {
    const { name, value } = e.target;
    setEditedCoach((prevCoach) => ({
      ...prevCoach,
      [name]: value,
    }));
  };

  const handleEditedMemberChange = (e) => {
    const { name, value } = e.target;
    setEditedMember((prevMember) => ({
      ...prevMember,
      [name]: value,
    }));
  };

  const handleSaveEditCoach = (id) => {
    if (editedCoach.name.trim()) {
      setFormData((prevData) => ({
        ...prevData,
        coaches: prevData.coaches.map((coach) =>
          coach.id === id ? { ...editedCoach } : coach
        ),
      }));
      setEditingCoachId(null);
      setEditedCoach({
        name: "",
        pricingPlan: "regular",
        bookingPeriod: "",
        dateRange: defaultRange,
        price: 80,
      });
    }
  };

  const handleSaveEditTeamMember = (id) => {
    if (editedMember.name.trim()) {
      setFormData((prevData) => ({
        ...prevData,
        teamMembers: prevData.teamMembers.map((member) =>
          member.id === id ? { ...editedMember } : member
        ),
      }));
      setEditingMemberId(null);
      setEditedMember({
        name: "",
        age: "",
        belt: "",
        pricingPlan: "regular",
        bookingPeriod: "",
        dateRange: defaultRange,
        price: 80,
      });
    }
  };

  const handleCancelEditCoach = () => {
    restoreDatepicker();
    setEditingCoachId(null);
    setEditedCoach({
      name: "",
      pricingPlan: "regular",
      bookingPeriod: "",
      dateRange: defaultRange,
      price: 80,
    });
  };

  const handleCancelEdit = () => {
    restoreDatepicker();
    setEditingMemberId(null);
    setEditedMember({
      name: "",
      age: "",
      belt: "",
      pricingPlan: "regular",
      bookingPeriod: "",
      dateRange: defaultRange,
      price: 80,
    });
  };

  const handleDeleteCoach = (id) => {
    setFormData((prevData) => ({
      ...prevData,
      coaches: prevData.coaches.filter((coach) => coach.id !== id),
    }));
  };

  const handleDeleteTeamMember = (id) => {
    setFormData((prevData) => ({
      ...prevData,
      teamMembers: prevData.teamMembers.filter((member) => member.id !== id),
    }));
  };

  const sendEmail = (e) => {
    e.preventDefault();

    const coachList = formData.coaches
      .map((coach, index) => {
        if (coach.pricingPlan === "full") {
          return `${index + 1}. ${coach.name}  (Ticket: ${
            coach.pricingPlan
          }, Booking period: ${coach.bookingPeriod},
          Price: ${coach.price}€)`;
        }
        return `${index + 1}. ${coach.name}  (Ticket: ${coach.pricingPlan},
        Price: ${coach.price}€)`;
      })
      .join("\n");

    const teamList = formData.teamMembers
      .map((member, index) => {
        if (member.pricingPlan === "full") {
          return `${index + 1}. ${member.name} (Age: ${member.age}, Belt: ${
            member.belt
          }, Ticket: ${member.pricingPlan}, Booking period: ${
            member.bookingPeriod
          }, Price: ${member.price}€)`;
        }
        return `${index + 1}. ${member.name} (Age: ${member.age}, Belt: ${
          member.belt
        }, Ticket: ${member.pricingPlan}, Price: ${member.price}€)`;
      })
      .join("\n");

    let templateParams = {};

    switch (formData.applicationType) {
      case "team":
        templateParams = {
          name: formData.clubName,
          email: formData.clubEmail,
          phone: formData.clubPhone,
          message: `
          Coaches:
           ${coachList}
           
          Team Members:
          ${teamList}
          Application type: TEAM
          Total Amount: ${formData.totalAmount}€
        `,
        };
        break;

      case "individual":
        if (formData.individualPricingPlan === "full") {
          templateParams = {
            name: formData.individualName,
            email: formData.individualEmail,
            phone: formData.individualPhone,
            message: `
            Club: ${formData.individualClubName}
            Age: ${formData.individualAge}
            Belt: ${formData.individualBelt}
            Ticket: ${formData.individualPricingPlan}
            Booking period: ${formData.individualBookingPeriod}
            Price: ${formData.totalAmount}€
            Application type: INDIVIDUAL
          `,
          };
        } else {
          templateParams = {
            name: formData.individualName,
            email: formData.individualEmail,
            phone: formData.individualPhone,
            message: `
            Club: ${formData.individualClubName}
            Age: ${formData.individualAge}
            Belt: ${formData.individualBelt}
            Ticket: ${formData.individualPricingPlan}
            Price: 80€
            Application type: INDIVIDUAL
          `,
          };
        }
        break;

      case "volunteer":
        templateParams = {
          name: formData.volunteerName,
          email: formData.volunteerEmail,
          phone: formData.volunteerPhone,
          message: `
          Club: ${formData.volunteerClubName}
          Age: ${formData.volunteerAge}
          Application type: VOLUNTEER
        `,
        };
        break;

      default:
        console.warn("Unknown application type:", formData.applicationType);
        break;
    }

    // console.log(templateParams);

    emailjs
      .send(
        "service_mg0ow2e", // ← from EmailJS
        "template_viqzxt5", // ← from EmailJS
        templateParams,
        "IncJQUK9dxF5n1Qg7" // ← from EmailJS (User ID)
      )
      .then(
        (result) => {
          // console.log("SUCCESS!", result.text);
          navigate("/", {
            state: {
              modalType: "success",
              message: t("contactForm.successMessage"),
            },
          });
        },
        (error) => {
          // console.log("FAILED...", error.text);
          navigate("/", {
            state: {
              modalType: "error",
              message: t("contactForm.errorMessage"),
            },
          });
        }
      );
  };

  // const handleLog = () => {
  //   console.log(formData);
  // };

  return (
    <div className="contact-form-container">
      <div className="contact-form">
        {/* <button onClick={handleLog}>log data</button> */}
        <div className="title-container">
          <h3>{t("contactForm.application")}</h3>

          {/* <p className="text date"> {t("applicationEndDate3")}</p>
          <p className="text limit">{t("applicationEndDate4")}</p> */}

          <button className="go-back-btn" onClick={handleGoBack}>
            <span className="default-icon">
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 18.6274L18.6274 2.00002"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M2 2L18.6274 18.6274"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>

              {/* <img src={iconClose} alt="" /> */}
            </span>
            <span className="hover-icon">
              {/* <img src={iconCloseHover} alt="" /> */}
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 18.6274L18.6274 2.00002"
                  stroke="#285770"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M2 2L18.6274 18.6274"
                  stroke="#285770"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        </div>

        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your message!</h2>
            <p>We will get back to you soon.</p>
          </div>
        ) : (
          <form className="" ref={form} onSubmit={sendEmail}>
            {/* select type of application */}
            <div className="application-type-selector">
              <button
                className={`application-type-button ${
                  applicationType === "team" ? "active" : ""
                }`}
                onClick={() => handleApplicationTypeChange("team")}
              >
                {t("contactForm.clubApplication")}
              </button>
              <button
                className={`application-type-button ${
                  applicationType === "individual" ? "active" : ""
                }`}
                onClick={() => handleApplicationTypeChange("individual")}
              >
                {t("contactForm.individualApplication")}
              </button>

              <button
                className={`application-type-button ${
                  applicationType === "volunteer" ? "active" : ""
                }`}
                onClick={() => handleApplicationTypeChange("volunteer")}
              >
                {t("contactForm.volunteerApplication")}
              </button>
            </div>

            {/* TEAM application */}
            {applicationType === "team" && (
              <div className="team-application">
                <div className="club-info">
                  <div className="form-group">
                    <label htmlFor="clubName">
                      {t("contactForm.clubName")}:
                    </label>
                    <input
                      type="text"
                      id="clubName"
                      name="clubName"
                      value={formData.clubName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* <div className="form-group">
                    <label htmlFor="coachName">
                      {t("contactForm.coachName")}:
                    </label>
                    <input
                      type="text"
                      id="coachName"
                      name="coachName"
                      value={formData.coachName}
                      onChange={handleChange}
                      required
                    />
                  </div> */}

                  <div className="form-group">
                    <label htmlFor="clubPhone">{t("contactForm.phone")}:</label>
                    <input
                      type="text"
                      id="clubPhone"
                      name="clubPhone"
                      value={formData.clubPhone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="clubEmail">Email:</label>
                    <input
                      type="email"
                      id="clubEmail"
                      name="clubEmail"
                      value={formData.clubEmail}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* coaches list */}
                {formData.coaches.length > 0 && (
                  <div className="team-members-list">
                    <h3>{t("contactForm.coaches")}:</h3>
                    <ul>
                      {formData.coaches.map((coach) => (
                        <li key={coach.id}>
                          {editingCoachId === coach.id ? (
                            <div className="team-members-edit-container">
                              <div className="input-fields">
                                <label htmlFor={`editName-${coach.id}`}>
                                  {t("contactForm.fullName")}:
                                </label>
                                <input
                                  type="text"
                                  id={`editName-${coach.id}`}
                                  name="name"
                                  value={editedCoach.name}
                                  onChange={handleEditedCoachChange}
                                />
                              </div>

                              {/* Pricing plan for coach */}
                              <div className="form-group pricing-plan-container">
                                <label>{t("contactForm.pricingPlan")}:</label>

                                <div className="dropdown" ref={dropdownRef}>
                                  <div
                                    className="dropdown-button"
                                    onClick={toggleDropdown}
                                  >
                                    {editedCoach.pricingPlan}
                                    <span className="arrow">
                                      <img src={arrowDown} alt="Arrow down" />
                                    </span>
                                  </div>
                                  {isOpen && (
                                    <div className="dropdown-options">
                                      {options.map((option) => (
                                        <div
                                          key={option}
                                          className={`dropdown-option ${
                                            editedCoach.pricingPlan === option
                                              ? "selected"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            handleEditPricingPlanChange(option)
                                          }
                                        >
                                          {option}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {editedCoach.pricingPlan === "full" && (
                                  <div className="form-group date-range-picker">
                                    <label>
                                      {t("contactForm.selectDates")}:
                                    </label>
                                    <DateRange
                                      editableDateInputs={false}
                                      onChange={handleEditBookingPeriodChange}
                                      moveRangeOnFirstSelection={false}
                                      ranges={editedCoach.dateRange}
                                      minDate={new Date(2025, 5, 4)}
                                      maxDate={new Date(2025, 5, 8)}
                                      shownDate={new Date(2025, 5, 4)}
                                      locale={enUS}
                                      rangeColors={["#00ABFF"]}
                                    />
                                    {numOfNights < 1 && (
                                      <p className="selection-error">
                                        {t("contactForm.selectDatesError")}
                                      </p>
                                    )}
                                    <div className="price-summary">
                                      <AccommodationTypeSelector
                                        selectedRate={
                                          editedCoach.accommodationRate ??
                                          DEFAULT_ACCOMMODATION_RATE
                                        }
                                        onSelect={
                                          handleEditAccommodationRateChange
                                        }
                                      />
                                      <p>
                                        {t("contactForm.accommodationPrice")}:{" "}
                                        <strong>
                                          {calcAccommodationPrice(
                                            numOfNights,
                                            editedCoach.accommodationRate ??
                                              DEFAULT_ACCOMMODATION_RATE
                                          )}{" "}
                                          €
                                        </strong>
                                      </p>
                                      <p>
                                        {t("contactForm.participationPrice")}:{" "}
                                        <strong>80 €</strong>
                                      </p>
                                      <p>
                                        {t("contactForm.total")}:{" "}
                                        <strong>{editedCoach.price} €</strong>
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="buttons">
                                <button
                                  type="button"
                                  className="btn-edit-action save"
                                  disabled={
                                    editedCoach.pricingPlan === "full" &&
                                    numOfNights < 1
                                  }
                                  onClick={() => handleSaveEditCoach(coach.id)}
                                >
                                  {t("contactForm.save")}
                                </button>
                                <button
                                  type="button"
                                  className="btn-edit-action save"
                                  onClick={handleCancelEditCoach}
                                >
                                  {t("contactForm.cancel")}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="team-members-item">
                              <p>
                                {coach.name} &nbsp;&nbsp; {coach.price}€
                              </p>

                              <div className="buttons">
                                <button
                                  type="button"
                                  onClick={() => handleShowEditCoach(coach)}
                                >
                                  {/* <span> */}
                                  <img src={editIcon} alt="" />
                                  {/* </span> */}
                                  {/* Edit */}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCoach(coach.id)}
                                >
                                  <img src={deleteIcon} alt="" />
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* adding coach */}
                {!isAddingCoach && (
                  <button
                    type="button"
                    className="btn add-team-member coach"
                    onClick={handleShowAddCoach}
                    disabled={isAddingMember}
                  >
                    <span>+</span> {t("contactForm.addCoach")}
                  </button>
                )}

                {isAddingCoach && (
                  <div className="add-member-section">
                    <h3>{t("contactForm.addingCoach")}</h3>
                    <div className="discount">
                      <p>
                        {t("discount1")}
                        <span>{t("discount2")}</span>
                        {t("discount3")}.
                      </p>
                    </div>
                    <label htmlFor="newMemberName">
                      {t("contactForm.coachName")}:
                    </label>
                    <input
                      type="text"
                      id="newMemberName"
                      name="name"
                      value={newCoach.name}
                      onChange={handleNewCoachChange}
                    />

                    {/* Pricing plan for adding coach */}
                    <div className="form-group pricing-plan-container">
                      <label>{t("contactForm.pricingPlan")}:</label>

                      <div className="dropdown" ref={dropdownRef}>
                        <div
                          className="dropdown-button"
                          onClick={toggleDropdown}
                        >
                          {pricingPlan}
                          <span className="arrow">
                            <img src={arrowDown} alt="Arrow down" />
                          </span>
                        </div>
                        {isOpen && (
                          <div className="dropdown-options">
                            {options.map((option) => (
                              <div
                                key={option}
                                className={`dropdown-option ${
                                  pricingPlan === option ? "selected" : ""
                                }`}
                                onClick={() => handlePricingPlanChange(option)}
                              >
                                {option}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {pricingPlan === "full" && (
                        <div className="form-group date-range-picker">
                          <label> {t("contactForm.selectDates")}:</label>
                          <DateRange
                            editableDateInputs={false}
                            onChange={handleBookingPeriodChange}
                            moveRangeOnFirstSelection={false}
                            ranges={dateRange}
                            minDate={new Date(2025, 5, 4)}
                            maxDate={new Date(2025, 5, 8)}
                            shownDate={new Date(2025, 5, 4)}
                            locale={enUS}
                            rangeColors={["#00ABFF"]}
                          />
                          {numOfNights < 1 && (
                            <p className="selection-error">
                              {t("contactForm.selectDatesError")}
                            </p>
                          )}
                          <div className="price-summary">
                            <AccommodationTypeSelector
                              selectedRate={accommodationRate}
                              onSelect={handleAccommodationRateChange}
                            />
                            <p>
                              {t("contactForm.accommodationPrice")}:{" "}
                              <strong>
                                {calcAccommodationPrice(
                                  numOfNights,
                                  accommodationRate
                                )}{" "}
                                €
                              </strong>
                            </p>
                            <p>
                              {t("contactForm.participationPrice")}:{" "}
                              <strong>80 €</strong>
                            </p>
                            <p>
                              {t("contactForm.total")}:{" "}
                              <strong>{newCoach.price} €</strong>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-form-action"
                        onClick={handleAddCoach}
                        disabled={pricingPlan === "full" && numOfNights < 1}
                      >
                        {t("contactForm.add")}
                      </button>
                      <button
                        type="button"
                        className="btn-form-action"
                        onClick={handlecancelAddCoach}
                      >
                        {t("contactForm.cancel")}
                      </button>
                    </div>
                  </div>
                )}

                {/* team members list */}
                {formData.teamMembers.length > 0 && (
                  <div className="team-members-list">
                    <h3>{t("contactForm.teamMembers")}:</h3>
                    <ul>
                      {formData.teamMembers.map((member) => (
                        <li key={member.id}>
                          {editingMemberId === member.id ? (
                            <div className="team-members-edit-container">
                              <div className="input-fields">
                                <label htmlFor={`editName-${member.id}`}>
                                  {t("contactForm.fullName")}:
                                </label>
                                <input
                                  type="text"
                                  id={`editName-${member.id}`}
                                  name="name"
                                  value={editedMember.name}
                                  onChange={handleEditedMemberChange}
                                />
                                <label htmlFor={`editAge-${member.id}`}>
                                  {t("contactForm.dateOfBirth")}:
                                </label>
                                <input
                                  type="number"
                                  id={`editAge-${member.id}`}
                                  name="age"
                                  value={editedMember.age}
                                  onChange={handleEditedMemberChange}
                                />
                                <label htmlFor={`editBelt-${member.id}`}>
                                  {t("contactForm.belt")}:
                                </label>
                                <input
                                  type="text"
                                  id={`editBelt-${member.id}`}
                                  name="belt"
                                  value={editedMember.belt}
                                  onChange={handleEditedMemberChange}
                                />
                              </div>

                              {/* Pricing plan for member */}
                              <div className="form-group pricing-plan-container">
                                <label>{t("contactForm.pricingPlan")}:</label>

                                <div className="dropdown" ref={dropdownRef}>
                                  <div
                                    className="dropdown-button"
                                    onClick={toggleDropdown}
                                  >
                                    {editedMember.pricingPlan}
                                    <span className="arrow">
                                      <img src={arrowDown} alt="Arrow down" />
                                    </span>
                                  </div>
                                  {isOpen && (
                                    <div className="dropdown-options">
                                      {options.map((option) => (
                                        <div
                                          key={option}
                                          className={`dropdown-option ${
                                            editedMember.pricingPlan === option
                                              ? "selected"
                                              : ""
                                          }`}
                                          onClick={() =>
                                            handleEditPricingPlanChange(option)
                                          }
                                        >
                                          {option}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {editedMember.pricingPlan === "full" && (
                                  <div className="form-group date-range-picker">
                                    <label>
                                      {t("contactForm.selectDates")}:
                                    </label>
                                    <DateRange
                                      editableDateInputs={false}
                                      onChange={handleEditBookingPeriodChange}
                                      moveRangeOnFirstSelection={false}
                                      ranges={editedMember.dateRange}
                                      minDate={new Date(2025, 5, 4)}
                                      maxDate={new Date(2025, 5, 8)}
                                      shownDate={new Date(2025, 5, 4)}
                                      locale={enUS}
                                      rangeColors={["#00ABFF"]}
                                    />
                                    {numOfNights < 1 && (
                                      <p className="selection-error">
                                        {t("contactForm.selectDatesError")}
                                      </p>
                                    )}
                                    <div className="price-summary">
                                      <AccommodationTypeSelector
                                        selectedRate={
                                          editedMember.accommodationRate ??
                                          DEFAULT_ACCOMMODATION_RATE
                                        }
                                        onSelect={
                                          handleEditAccommodationRateChange
                                        }
                                      />
                                      <p>
                                        {t("contactForm.accommodationPrice")}:{" "}
                                        <strong>
                                          {calcAccommodationPrice(
                                            numOfNights,
                                            editedMember.accommodationRate ??
                                              DEFAULT_ACCOMMODATION_RATE
                                          )}{" "}
                                          €
                                        </strong>
                                      </p>
                                      <p>
                                        {t("contactForm.participationPrice")}:{" "}
                                        <strong>80 €</strong>
                                      </p>
                                      <p>
                                        {t("contactForm.total")}:{" "}
                                        <strong>{editedMember.price} €</strong>
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="buttons">
                                <button
                                  type="button"
                                  className="btn-edit-action save"
                                  onClick={() =>
                                    handleSaveEditTeamMember(member.id)
                                  }
                                >
                                  {t("contactForm.save")}
                                </button>
                                <button
                                  type="button"
                                  className="btn-edit-action cancel"
                                  onClick={handleCancelEdit}
                                >
                                  {t("contactForm.cancel")}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="team-members-item">
                              {/* {member.name} (Age: {member.age}, Belt: {member.belt}) */}
                              <p>
                                {member.name} &nbsp;&nbsp; {member.price}€
                              </p>
                              <div className="buttons">
                                <button
                                  type="button"
                                  onClick={() => handleShowEditMember(member)}
                                >
                                  {/* <span> */}
                                  <img src={editIcon} alt="" />
                                  {/* </span> */}
                                  {/* Edit */}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDeleteTeamMember(member.id)
                                  }
                                >
                                  <img src={deleteIcon} alt="" />
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!isAddingMember && (
                  <button
                    type="button"
                    className="btn add-team-member"
                    onClick={handleShowAddMember}
                    disabled={isAddingCoach}
                  >
                    <span>+</span> {t("contactForm.addTeamMember")}
                  </button>
                )}

                {isAddingMember && (
                  <div className="add-member-section">
                    <h3>{t("contactForm.addingTeamMember")}</h3>
                    <label htmlFor="newMemberName">
                      {t("contactForm.fullName")}:
                    </label>
                    <input
                      type="text"
                      id="newMemberName"
                      name="name"
                      value={newMember.name}
                      onChange={handleNewMemberChange}
                    />
                    <label htmlFor="newMemberAge">
                      {t("contactForm.dateOfBirth")}:
                    </label>
                    <input
                      type="number"
                      id="newMemberAge"
                      name="age"
                      value={newMember.age}
                      onChange={handleNewMemberChange}
                    />
                    <label htmlFor="newMemberBelt">
                      {t("contactForm.belt")}:
                    </label>
                    <input
                      type="text"
                      id="newMemberBelt"
                      name="belt"
                      value={newMember.belt}
                      onChange={handleNewMemberChange}
                    />

                    {/* Pricing plan for adding team member */}
                    <div className="form-group pricing-plan-container">
                      <label>{t("contactForm.pricingPlan")}:</label>

                      <div className="dropdown" ref={dropdownRef}>
                        <div
                          className="dropdown-button"
                          onClick={toggleDropdown}
                        >
                          {pricingPlan}
                          <span className="arrow">
                            <img src={arrowDown} alt="Arrow down" />
                          </span>
                        </div>
                        {isOpen && (
                          <div className="dropdown-options">
                            {options.map((option) => (
                              <div
                                key={option}
                                className={`dropdown-option ${
                                  pricingPlan === option ? "selected" : ""
                                }`}
                                onClick={() => handlePricingPlanChange(option)}
                              >
                                {option}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {pricingPlan === "full" && (
                        <div className="form-group date-range-picker">
                          <label> {t("contactForm.selectDates")}:</label>
                          <DateRange
                            editableDateInputs={false}
                            onChange={handleBookingPeriodChange}
                            moveRangeOnFirstSelection={false}
                            ranges={dateRange}
                            minDate={new Date(2025, 5, 4)}
                            maxDate={new Date(2025, 5, 8)}
                            shownDate={new Date(2025, 5, 4)}
                            locale={enUS}
                            rangeColors={["#00ABFF"]}
                          />
                          {numOfNights < 1 && (
                            <p className="selection-error">
                              {t("contactForm.selectDatesError")}
                            </p>
                          )}
                          <div className="price-summary">
                            <AccommodationTypeSelector
                              selectedRate={accommodationRate}
                              onSelect={handleAccommodationRateChange}
                            />
                            <p>
                              {t("contactForm.accommodationPrice")}:{" "}
                              <strong>
                                {calcAccommodationPrice(
                                  numOfNights,
                                  accommodationRate
                                )}{" "}
                                €
                              </strong>
                            </p>
                            <p>
                              {t("contactForm.participationPrice")}:{" "}
                              <strong>80 €</strong>
                            </p>
                            <p>
                              {t("contactForm.total")}:{" "}
                              <strong>{newMember.price} €</strong>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        onClick={handleAddTeamMember}
                        disabled={pricingPlan === "full" && numOfNights < 1}
                      >
                        {t("contactForm.add")}
                      </button>
                      <button type="button" onClick={handlecancelAddMember}>
                        {t("contactForm.cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* INDIVIDUAL application */}
            {applicationType === "individual" && (
              <div className="individual-application">
                <div className="form-group">
                  <label htmlFor="individualName">
                    {t("contactForm.fullName")}:
                  </label>
                  <input
                    type="text"
                    id="individualName"
                    name="individualName"
                    value={formData.individualName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="individualClubName">
                    {t("contactForm.clubName")}:
                  </label>
                  <input
                    type="text"
                    id="individualClubName"
                    name="individualClubName"
                    value={formData.individualClubName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="individualAge">
                    {t("contactForm.dateOfBirth")}:
                  </label>
                  <input
                    type="number"
                    id="individualAge"
                    name="individualAge"
                    value={newMember.individualAge}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="individualBelt">
                    {t("contactForm.belt")}:
                  </label>
                  <input
                    type="text"
                    id="individualBelt"
                    name="individualBelt"
                    value={formData.individualBelt}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="individualPhone">
                    {t("contactForm.phone")}:
                  </label>
                  <input
                    type="text"
                    id="individualPhone"
                    name="individualPhone"
                    value={formData.individualPhone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="individualEmail">Email:</label>
                  <input
                    type="individualEmail"
                    id="individualEmail"
                    name="individualEmail"
                    value={formData.individualEmail}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            {/* VOLUNTEER application */}
            {applicationType === "volunteer" && (
              <div className="individual-application">
                <div className="form-group">
                  <label htmlFor="volunteerName">
                    {t("contactForm.fullName")}:
                  </label>
                  <input
                    type="text"
                    id="volunteerName"
                    name="volunteerName"
                    value={formData.volunteerName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="volunteerClubName">
                    {t("contactForm.clubName")}:
                  </label>
                  <input
                    type="text"
                    id="volunteerClubName"
                    name="volunteerClubName"
                    value={formData.volunteerClubName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="volunteerAge">
                    {t("contactForm.dateOfBirth")}:
                  </label>
                  <input
                    type="number"
                    id="volunteerAge"
                    name="volunteerAge"
                    value={newMember.volunteerAge}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="volunteerPhone">
                    {t("contactForm.phone")}:
                  </label>
                  <input
                    type="text"
                    id="volunteerPhone"
                    name="volunteerPhone"
                    value={formData.volunteerPhone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="volunteerEmail">Email:</label>
                  <input
                    type="volunteerEmail"
                    id="volunteerEmail"
                    name="volunteerEmail"
                    value={formData.volunteerEmail}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            {/* Individual pricing */}
            {applicationType === "individual" && (
              <div className="form-group pricing-plan-container">
                <label>{t("contactForm.pricingPlan")}:</label>

                <div className="dropdown" ref={dropdownRef}>
                  <div className="dropdown-button" onClick={toggleDropdown}>
                    {pricingPlan}
                    <span className="arrow">
                      <img src={arrowDown} alt="Arrow down" />
                    </span>
                  </div>
                  {isOpen && (
                    <div className="dropdown-options">
                      {options.map((option) => (
                        <div
                          key={option}
                          className={`dropdown-option ${
                            pricingPlan === option ? "selected" : ""
                          }`}
                          onClick={() =>
                            handleIndividualPricingPlanChange(option)
                          }
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {pricingPlan === "full" && (
                  <div className="form-group date-range-picker">
                    <label> {t("contactForm.selectDates")}:</label>
                    <DateRange
                      editableDateInputs={false}
                      onChange={handleIndividualBookingPeriodChange}
                      moveRangeOnFirstSelection={false}
                      ranges={dateRange}
                      minDate={new Date(2025, 5, 4)}
                      maxDate={new Date(2025, 5, 8)}
                      shownDate={new Date(2025, 5, 4)}
                      locale={enUS}
                      rangeColors={["#00ABFF"]}
                    />
                    {numOfNights < 1 && (
                      <p className="selection-error">
                        {t("contactForm.selectDatesError")}
                      </p>
                    )}
                    <div className="price-summary">
                      <AccommodationTypeSelector
                        selectedRate={accommodationRate}
                        onSelect={handleIndividualAccommodationRateChange}
                      />
                      <p>
                        {t("contactForm.accommodationPrice")}:{" "}
                        <strong>
                          {calcAccommodationPrice(
                            numOfNights,
                            accommodationRate
                          )}{" "}
                          €
                        </strong>
                      </p>
                      <p>
                        {t("contactForm.participationPrice")}:{" "}
                        <strong>80 €</strong>
                      </p>
                      {/* <p>
                      {t("contactForm.total")}:{" "}
                      <strong>{newCoach.price} €</strong>
                    </p> */}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* {applicationType === "individual" && (
              <div className="form-group pricing-plan-container">
                <label>{t("contactForm.pricingPlan")}:</label>

                <div className="dropdown" ref={dropdownRef}>
                  <div className="dropdown-button" onClick={toggleDropdown}>
                    {pricingPlan}
                    <span className="arrow">
                      <img src={arrowDown} alt="Arrow down" />
                    </span>
                  </div>
                  {isOpen && (
                    <div className="dropdown-options">
                      {options.map((option) => (
                        <div
                          key={option}
                          className={`dropdown-option ${
                            pricingPlan === option ? "selected" : ""
                          }`}
                          onClick={() => handlePricingPlanChange(option)}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )} */}

            {error && <p className="error-message">{error}</p>}

            {applicationType !== "volunteer" && (
              <p>
                {t("contactForm.totalAmount")}: {totalAmount}€
              </p>
            )}

            <div className="form-group">
              {/* <button type="submit" className="submit-btn" onClick={() => console.log(formData)}> */}
              <button type="submit" className="submit-btn" onClick={sendEmail}>
                {t("contactForm.submit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomForm;
